<template>
  <div
    class="d-flex mh-0 mw-0"
    style="gap: 1rem"
    @mouseleave="showTooltip = false"
    @touchend="showTooltip = false"
  >
    <div class="d-flex icmstckschrt mh-0 mw-0">
      <LoaderCircles
        v-if="data_call.loading"
        style="z-index: 6; position: absolute; top: 1rem; right: 1rem; --size: 1.5rem"
        class="anim-fade-in"
      />
      <IconAndText
        v-if="data_call.error"
        class="ma-auto pa-4"
        style="--icon-size: 5rem; --icon-color: var(--clr-error)"
        icon="mdi-alert-circle-outline"
        :title="$t('messages.oops_error')"
      />

      <div
        v-show="!data_call.error"
        ref="chart"
        class="chart"
        :style="`--transition-column:${chartData.length < 555 ? 0.22 : 0}s`"
        @mousemove="
          e => {
            showTooltip = true
            handleMousemove(e)
          }
        "
        @touchmove="
          e => {
            showTooltip = true
            handleMousemove(e)
          }
        "
        @mouseleave="showTooltip = false"
        @touchend="showTooltip = false"
      >
        <div
          v-if="showTooltip"
          ref="tooltip"
          style="position: fixed; transition: 0.22s; z-index: 9; box-shadow: var(--menu-shadow)"
          class="gw_box no-events d-flex text-medium weight_700"
        >
          <div
            v-if="tooltipItem?.label"
            class="mr-2 f-center"
          >
            <div
              class="circle-bullet mr-2"
              style="--size: 0.5rem"
              :style="tooltipItem.style"
            ></div>
            {{ tooltipItem.label }}
          </div>
          {{ numFixedUnit(tooltipItem?.stack, 'bb') }}bb
        </div>
        <div class="position-relative d-flex flex-1">
          <ChartAxes
            class="repchart_axes_chart_axis"
            style="
              --axis-space: 0px;
              --axis-y-width: 0px;
              --axis-height: 1rem;
              --axis-x-inset: auto auto -1rem 0;
              --clr-axis-line-tick: rgba(0, 0, 0, 0.25);
              --first-tick-opacity: 0;
              --axis-y-justify-content: flex-end;
              --axis-y-padding: 0 0.325rem 0 0;
            "
            :YTicks
            :hasX="true"
            :hasLineY="true"
            :hasLinesY="false"
          >
            <template #legend_x>
              <div
                class="clr-grey text-small"
                style="
                  letter-spacing: 0.5px;
                  position: absolute;
                  left: 50%;
                  transform: translate(-50%, 1.25rem);
                "
              >
                {{ $t('solfigr.icm.stack_percentile') }}
              </div>
            </template>
            <template #legend_y>
              <div
                class="clr-grey text-small"
                style="
                  letter-spacing: 0.5px;
                  position: absolute;
                  top: 50%;
                  writing-mode: vertical-rl;
                  transform: translate(-2.75rem, -50%) rotate(180deg);
                "
              >
                {{ $t('solfigr.icm.bb') }}
              </div>
            </template>
          </ChartAxes>
          <div
            v-for="(col, index) in chartData"
            :key="index"
            class="chart_column"
            :style="col.style"
            :data-tst="`icmstckschrt_column_${index}${col.label ? `_${col.position}` : ''}`"
            @mouseenter="
              () => {
                tooltipItem = chartData[index]
              }
            "
          ></div>
        </div>
      </div>
    </div>
    <div class="f-column">
      <div class="d-flex mx-auto flex-1">
        <RangeInput
          direction="vertical"
          :values="[
            {
              id: 0,
              cssStyle: `
            --clr-input-back: var(--clr-primary);
            --clr-input-handle: var(--clr-primary);
            `,
              percentage: logDeviationPercentage,
            },
            {
              id: 1,
              cssStyle: `--clr-input-back: var(--clr-black-3);`,
              percentage: 1 - logDeviationPercentage,
            },
          ]"
          @values:update="({ values }) => handleInput(values)"
        />
      </div>
      <div class="clr-grey text-medium mt-2">{{ $t('solfigr.icm.lognormal_deviation') }}</div>
    </div>
  </div>
</template>

<script>
import IconAndText from '@/components/Poker/Overlays/IconAndText'
import LoaderCircles from '@/components/Loaders/LoaderCircles'
import { getSolfigrFields, getAvgStackIpOopIncluded } from '@/store/solfigr/solfigr_helpers'
import { throttle } from 'lodash'
import ChartAxes from '@/components/Charts/LineCharts/ChartAxes'
import RangeInput from '@/components/lib/RangeInput'
import format_mix from '@/mixins/format_mix'
import fetch_mix from '@/mixins/fetch_mix'
import float_mix from '@/mixins/float_mix'

export default {
  name: 'SolfigrIcmStacksDistributionChart',
  components: {
    ChartAxes,
    RangeInput,
    IconAndText,
    LoaderCircles,
  },
  mixins: [fetch_mix, format_mix, float_mix],
  props: {
    loading: Boolean,
    remainingPlayers: Number,
    logNormalDeviation: Number,
    avgStack: Number,
    stackOOP: Number,
    stackIP: Number,
    playerIP: Object,
    playerOOP: Object,
  },
  emits: ['update:values'],
  data() {
    this.conf = getSolfigrFields()
    return {
      data_call: {
        conf: {
          method: 'get',
          endpoint: 'tournaments/chip-distribution/',
          apiVersion: 'v4',
        },
        loading: false,
        loaded: false,
        status: 0,
        error: null,
        hasCancel: true,
      },
      chartData: [],
      logDeviationPercentage: null,
      tooltipItem: null,
      showTooltip: false,
    }
  },
  created() {
    this.logDeviationPercentage = this.conf.lognormal_deviation.getPercentageFromValue(
      this.logNormalDeviation,
    )
  },
  methods: {
    handleInput(values) {
      this.logDeviationPercentage = values[0].percentage
    },
    getData: throttle(async function () {
      if (!this.params) return
      if (this.hasIpOopStacks && this.remainingPlayers === 2) {
        this.setChartData({ stacks: [] })
        return
      }

      this.data_call.conf.params = this.params
      const { data, error } = await this.fch_getData(this.data_call)
      if (this.data_call.canceled) {
        this.data_call.loading = true
        this.data_call.error = null
        return
      }
      if (error) {
        return
      }
      this.setChartData(data)

      this.$emit('update:values', {
        stacks_defined: data.stacks,
        lognormal_deviation: this.lognormalDeviation,
      })
    }, 200),
    handleMousemove: throttle(function (e) {
      const boundary = e.currentTarget
      const { tooltip } = this.$refs
      if (!tooltip) return
      e.preventDefault()
      this.fl_followMouse(e, boundary, tooltip, {
        offsetX: 8,
        padding: 8,
        strategy: 'fixed',
        placement: 'top-start',
      })
    }, 10),
    setChartData(data) {
      this.chartData = data

      const maxNumber = Math.max(
        ...(this.hasIpOopStacks ? [...data.stacks, this.stackIP, this.stackOOP] : data.stacks),
      )

      this.chartData = data.stacks.map(stack => ({
        stack: Number(stack),
        style: {
          '--col-height': `${(stack / maxNumber) * 100}%`,
        },
      }))

      if (this.hasIpOopStacks) {
        this.chartData.push({
          stack: this.stackIP,
          label: this.playerIP.display_position,
          position: 'IP',
          style: {
            '--col-height': `${(this.stackIP / maxNumber) * 100}%`,
            '--col-dot-clr': 'var(--clr-player_ip)',
            color: 'var(--clr-player_ip)',
          },
        })
        this.chartData.push({
          stack: this.stackOOP,
          label: this.playerOOP.display_position,
          position: 'OOP',
          style: {
            '--col-height': `${(this.stackOOP / maxNumber) * 100}%`,
            '--col-dot-clr': 'var(--clr-player_oop)',
            color: 'var(--clr-player_oop)',
          },
        })
      }

      this.chartData = this.chartData.sort((a, b) => (a.stack < b.stack ? 1 : -1))
    },
  },
  computed: {
    YTicks() {
      let res = []
      let data = [...this.chartData.map(i => i.stack).map(i => Number(Number(i).toFixed()))]
      data = [...new Set(data)]

      if (!data.length) return []

      const max = Math.max(...data)
      const all = data.map(i => ({
        value: i,
        value_nrm: i / max,
        style: {
          position: 'absolute',
          transform: `translate(0, 50%)`,
          bottom: `${(i / max) * 100}%`,
        },
      }))
      all.reverse()

      // get only those values that are atleast 10% apart - max 10 ticks
      let lastKnownNnmValue = all[0].value_nrm
      const lastItemNrmValue = all[all.length - 1].value_nrm
      res = all.filter((i, index) => {
        if (
          lastKnownNnmValue - all[index - 1]?.value_nrm <= -0.1 &&
          lastItemNrmValue - i.value_nrm >= 0.1
        ) {
          lastKnownNnmValue = i.value_nrm
          return true
        }
        return false
      })

      res = [
        all[0], //
        ...res,
        all[all.length - 1],
      ]

      return res
    },
    hasIpOopStacks() {
      return !!(this.stackIP && this.stackOOP)
    },
    params() {
      if (this.logDeviationPercentage === null) return null

      return {
        players_left: this.hasIpOopStacks ? this.remainingPlayers - 2 : this.remainingPlayers,
        average_stack: this.hasIpOopStacks
          ? getAvgStackIpOopIncluded({
              stackIP: this.stackIP,
              stackOOP: this.stackOOP,
              avgStack: this.avgStack,
              remainingPlayers: this.remainingPlayers,
              avgStackMin: this.conf.avg_stack.min,
            })
          : this.avgStack,
        distribution_type: 'LogNormal',
        lognormal_deviation: this.lognormalDeviation,
      }
    },
    lognormalDeviation() {
      return (
        this.conf.lognormal_deviation.max -
        this.conf.lognormal_deviation.getValueFromPercentage(this.logDeviationPercentage)
      )
    },
  },
  watch: {
    params: {
      handler: 'getData',
      immediate: true,
    },
  },
}
</script>
<style lang="scss" scoped>
.icmstckschrt {
  width: 100%;
  height: 18rem;
  border: solid rgba(255, 255, 255, 0.1) 1px;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
}
.chart {
  display: flex;
  width: 100%;
  height: 100%;
  padding: 2.5rem 1rem 3rem 3rem;
  --transition-column: 0.22s;
  &_column {
    width: 100%;
    height: 100%;
    align-self: flex-end;
    position: relative;
    min-height: 1px;
    transition: var(--transition-column);
    --col-dot-clr: var(--clr-primary-2);
    &:before {
      content: '';
      width: clamp(1px, 100%, 0.35rem);
      padding: clamp(1px, 50%, 0.35rem);
      background: var(--col-dot-clr);
      border-radius: 50%;
      position: absolute;
      bottom: var(--col-height);
      transform: translate(-50%, 50%);
      left: 50%;
      z-index: 2;
    }
    &:after {
      content: '';
      width: 1px;
      height: var(--col-height);
      background: rgba(255, 255, 255, 0.1);
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translate(-50%, 0%);
    }

    border-radius: 4px 4px 0 0;
    transition: 0.22s;
    &:hover {
      background: rgba(255, 255, 255, 0.05);
    }
  }
}
</style>
